import { useMutation, useQuery, ApolloError } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Candidate, FieldConfig, MergePicker } from "components/MergePicker";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  ChemicalInput,
  DensityUnits,
  DocumentType,
  InventoryOverlapResolutionInput,
  InventoryOverlapResolutionStrategy,
  ProgramArea,
  PureOrMixture,
  WeightOrVolume,
} from "generated-graphql/graphql";
import { uniqBy } from "lodash-es";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { EhsFormLabels } from "util/ehsForm";
import {
  HealthHazardLabels,
  HealthHazardOrder,
  PhysicalHazardLabels,
  PhysicalHazardOrder,
} from "util/hazards";

import { useChemicalInputValidation } from "../../Report/validationHooks/useChemicalInputValidation";
import { ChemicalDetailInputs } from "../Chemical";
import {
  buildConflictChoiceSummaries,
  buildInventoryOverlapResolutionInputs,
  buildMergeImpact,
  CombineTargetProfile,
  countAffectedFacilities,
  findInventoryOverlaps,
  InventoryOverlap,
  inventoryOverlapKey,
  isBatchCombineEligible,
  InventoryOverlapResolutionState,
  MergeImpact,
  reconcileCombineDensityOverride,
  reconcileInventoryOverlapResolutions,
} from "./inventoryOverlaps";
import { MergeImpactSummary, MergeOutcome } from "./MergeImpactSummary";
import { ResolveInventoryOverlapsStep } from "./ResolveInventoryOverlapsStep";
import { buildReviewFieldSummaries } from "./reviewFieldSummaries";
import { ReviewMergeStep } from "./ReviewMergeStep";
import { useMergeHowToAnnouncement } from "./useMergeHowToAnnouncement";

const GET_CANDIDATE_CHEMICALS = gql(/* GraphQL */ `
  query GetCandidateChemicals($chemicalIds: [ID!]!) {
    chemicalsByIds(ids: $chemicalIds) {
      id
      name
      casNumber
      alternateId
      ehsForm
      stateOfMatter
      density
      densityUnits
      isEhs
      noHazardsNotReporting
      isTradeSecret
      manufacturerSupplier
      recommendedUse
      version
      revisionDate
      documents {
        id
        key
        name
        documentId
        document {
          id
          documentType
          title
          description
          storageLink
          fileExtension
          sizeMegabytes
          authoredAt
        }
      }
      pureOrMixture
      components {
        id
        name
        componentPercentage
        weightOrVolume
        casNumber
        isEhs
        noCasNumber
      }
      physicalHazards
      healthHazards
      stateFields {
        key
        value
        label
        jurisdiction
        tooltip
      }
      associatedFacilities {
        id
      }
      facilityChemicalStateFields {
        facilityId
        reportingYear
        jurisdiction
        key
        value
        label
      }
      facilityChemicals {
        id
        facilityId
        reportingYear
        maxAmount
        maxAmountLargestContainer
        unit
        facility {
          id
          name
          state
          tierIIReports {
            id
            reportingYear
            encampStatus
            reportKind
          }
        }
        storageLocations {
          id
          storageLocationId
          maxAmount
          averageAmount
          maxAmountLargestContainer
          unit
          pressure
          otherPressureValue
          temperature
          storageType
          storageTypes
          storageTypeDescription
          storageLocation {
            id
            description
          }
        }
      }
      measurementFacilityYears {
        facilityId
        facility {
          id
          name
          state
          tierIIReports {
            id
            reportingYear
            encampStatus
            reportKind
          }
        }
        reportingYear
        readingCount
        firstReadingAt
        lastReadingAt
        peaks {
          amount
          unit
        }
        storageLocations {
          id
          description
        }
      }
      issues {
        ...issue
      }
    }
  }
`);

const MERGE_CHEMICALS = gql(/* GraphQL */ `
  mutation MergeChemicals(
    $ids: [ID!]!
    $input: ChemicalInput!
    $acknowledgementReason: String
    $inventoryOverlapResolutions: [InventoryOverlapResolutionInput!]
    $acknowledgeFiledYearChanges: Boolean
  ) {
    mergeChemicals(
      ids: $ids
      input: $input
      acknowledgementReason: $acknowledgementReason
      inventoryOverlapResolutions: $inventoryOverlapResolutions
      acknowledgeFiledYearChanges: $acknowledgeFiledYearChanges
    ) {
      id
      name
    }
  }
`);

type ChemicalMergePickerProps = {
  open: boolean;
  onClose: () => void;
  chemicalIds: string[];
};

function exclusionConflictWithHazards(candidates: ChemicalInput[]): boolean {
  const values = new Set(candidates.map((c) => c.noHazardsNotReporting));
  const disagree = values.has(true) && values.has(false);
  const anyHazards = candidates.some(
    (c) => (c.physicalHazards?.length ?? 0) > 0 || (c.healthHazards?.length ?? 0) > 0
  );
  return disagree && anyHazards;
}

function mergeRequiresExclusionAcknowledgement(input: ChemicalInput): boolean {
  const hasHazards =
    (input.physicalHazards?.length ?? 0) > 0 || (input.healthHazards?.length ?? 0) > 0;
  return !!input.noHazardsNotReporting && hasHazards;
}

const extraInfoStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

/**
 * Summarizes what a merge did (or will do): the impact map (for the
 * "Affected scope" list and the outcome banner's unused-in-outcome-mode
 * `impact` prop) plus the outcome counts, broken out by resolved strategy.
 * Computed once at continueMerge time so it survives the pendingInventoryOverlaps
 * clear on success.
 */
function summarizeMergeOutcome(
  candidates: Parameters<typeof buildMergeImpact>[0],
  inventoryOverlaps: InventoryOverlap[],
  resolutions: Record<string, InventoryOverlapResolutionState>
): { impact: MergeImpact; outcome: MergeOutcome } {
  const impact = buildMergeImpact(candidates, inventoryOverlaps);
  let keptCount = 0;
  let combinedCount = 0;
  let blankCount = 0;
  for (const inventoryOverlap of inventoryOverlaps) {
    const strategy = resolutions[inventoryOverlapKey(inventoryOverlap)]?.strategy;
    if (strategy === InventoryOverlapResolutionStrategy.Keep) keptCount += 1;
    else if (strategy === InventoryOverlapResolutionStrategy.Combine) combinedCount += 1;
    else if (strategy === InventoryOverlapResolutionStrategy.Blank) blankCount += 1;
  }
  return {
    impact,
    outcome: { autoMergeCount: impact.autoMergeCount, keptCount, combinedCount, blankCount },
  };
}

export function ChemicalMergePicker(props: ChemicalMergePickerProps) {
  const alerts = useAlerts();

  const { shouldAutoOpen, markShown, markDismissed } = useMergeHowToAnnouncement(props.open);
  const [howToOpen, setHowToOpen] = useState(false);
  // Consumed the moment the How-to modal is opened or closed for any reason
  // (auto or manual) this dialog session. Without this, a status query that
  // is still in flight when the user manually opens then closes the modal
  // can resolve shouldAutoOpen=true afterwards and reopen a modal the user
  // just dismissed.
  const howToConsumedRef = useRef(false);

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const requestClose = () => setConfirmCancelOpen(true);

  useEffect(() => {
    if (props.open && shouldAutoOpen && !howToConsumedRef.current) {
      howToConsumedRef.current = true;
      setHowToOpen(true);
      void markShown();
    }
  }, [props.open, shouldAutoOpen, markShown]);

  const { data, loading: loadingChemicals } = useQuery(GET_CANDIDATE_CHEMICALS, {
    variables: {
      chemicalIds: props.chemicalIds,
    },
    // Overlap resolutions are validated server-side against current
    // inventory, so stale cached inventory could produce resolutions the
    // merge rejects.
    fetchPolicy: "network-only",
  });

  const [mergeChemicals, { loading: isMerging }] = useMutation(MERGE_CHEMICALS, {
    refetchQueries: ["TenantCatalogChemicals"],
  });

  const stateFieldFields = useMemo<FieldConfig<ChemicalInput>[]>(() => {
    return uniqBy(
      data?.chemicalsByIds.flatMap((chemical) =>
        chemical.stateFields?.filter((field) => hasValue(field.value))
      ) ?? [],
      (stateField) => stateField?.key
    )
      .sort((a, b) => {
        const jurisdictionA = a?.jurisdiction ?? "";
        const jurisdictionB = b?.jurisdiction ?? "";
        return jurisdictionA.localeCompare(jurisdictionB);
      })
      .map<FieldConfig<ChemicalInput>>((stateField) => ({
        name: stateField?.key ?? "",
        label: stateField?.label
          ? `State Field (${stateField.jurisdiction}):\n${stateField.label}`
          : "",
        strategy: "exclusive",
        merge: (selection, mergedRecord) => {
          const selectedStateField = selection.stateFields?.find(
            (sf) => sf.key === stateField?.key && hasValue(sf.value)
          );
          if (!selectedStateField) return mergedRecord;

          return {
            ...mergedRecord,
            stateFields: (
              mergedRecord.stateFields?.filter((sf) => sf.key !== stateField?.key) ?? []
            ).concat([{ ...selectedStateField }]),
          };
        },
        render(data) {
          const field = data.stateFields?.find((sf) => sf.key === stateField?.key);
          return typeof field?.value === "string"
            ? field.value
            : typeof field?.value === "boolean"
              ? field?.value
                ? "Yes"
                : "No"
              : (field?.value?.toString() ?? "");
        },
      }));
  }, [data?.chemicalsByIds]);

  const mergeFields = useMemo<FieldConfig<ChemicalInput>[]>(() => {
    return [
      {
        name: "chemName",
        label: "Chemical Name",
        strategy: "exclusive",
        sticky: true,
        primary: true,
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            name: selection.name,
          };
        },
        render(data) {
          return data.name ?? "";
        },
      },
      {
        name: "casNumber",
        label: "CAS Number",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            casNumber: selection.casNumber,
          };
        },
        render(data) {
          return data.casNumber ?? "";
        },
      },
      {
        name: "altID",
        label: "Alt ID",
        strategy: "inclusive",
        merge(selections, mergedRecord) {
          return {
            ...mergedRecord,
            alternateId: Array.from(
              new Set(selections.map((s) => s.alternateId).filter((v) => !!v))
            ).join(", "),
          };
        },
        render(data) {
          return data.alternateId ?? "";
        },
      },
      {
        name: "ehsForm",
        label: "EHS Form",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            ehsForm: selection.ehsForm,
          };
        },
        render(data) {
          return data.ehsForm ? EhsFormLabels[data.ehsForm] : "";
        },
      },
      {
        name: "stateOfMatter",
        label: "State of Matter",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            stateOfMatter: selection.stateOfMatter,
          };
        },
        render(data) {
          return data.stateOfMatter ? prettyPrintEnumValue(data.stateOfMatter) : "";
        },
      },
      {
        name: "density",
        label: "Density",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            density: selection.density,
          };
        },
        render(data) {
          return data.density ? data.density.toString() : "";
        },
      },
      {
        name: "densityUnits",
        label: "Density Units",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            densityUnits: selection.densityUnits,
          };
        },
        render(data) {
          return data.densityUnits ? prettyPrintEnumValue(data.densityUnits) : "";
        },
      },
      {
        name: "isEhs",
        label: "Extremely Hazardous",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            isEhs: selection.isEhs,
          };
        },
        render(data) {
          return hasValue(data.isEhs) ? (data.isEhs ? "Yes" : "No") : "";
        },
      },
      {
        name: "excludeFromTierII",
        label: "Exclude from Tier II",
        strategy: "exclusive",
        defaultIndex(candidates) {
          if (!exclusionConflictWithHazards(candidates)) return undefined;
          const safer = candidates.findIndex((c) => c.noHazardsNotReporting === false);
          return safer >= 0 ? safer : undefined;
        },
        warning(candidates) {
          return exclusionConflictWithHazards(candidates)
            ? "Sources disagree on Tier II exclusion and at least one has hazards listed. The safer 'No' option is preselected."
            : null;
        },
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            noHazardsNotReporting: selection.noHazardsNotReporting,
          };
        },
        render(data) {
          return hasValue(data.noHazardsNotReporting)
            ? data.noHazardsNotReporting
              ? "Yes"
              : "No"
            : "";
        },
      },
      {
        name: "isTradeSecret",
        label: "Trade Secret",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            isTradeSecret: selection.isTradeSecret,
          };
        },
        render(data) {
          return hasValue(data.isTradeSecret) ? (data.isTradeSecret ? "Yes" : "No") : "";
        },
      },
      {
        name: "sds",
        label: "SDS",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            documents: selection.documents,
          };
        },
        render(data) {
          const sds = data.documents?.find(
            (d) => d.document.documentType === DocumentType.SafetyDataSheet
          );
          return sds ? sds.name : "";
        },
      },
      {
        name: "pureOrMix",
        label: "Pure/Mix",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            components: selection.components,
            pureOrMixture: selection.pureOrMixture,
          };
        },
        render(data) {
          let str = data.pureOrMixture === PureOrMixture.Mixture ? "Mix" : "Pure";
          data.components?.forEach((component, i) => {
            str += `\n\nComponent ${i + 1}:\n${component.name}\n${
              component.componentPercentage
            }% by ${
              component.weightOrVolume === WeightOrVolume.Weight ? "weight" : "volume"
            }\n${component.casNumber}${component.isEhs ? "\nEHS" : ""}`;
          });
          return str;
        },
      },
      {
        name: "physicalHazards",
        label: "Physical Hazards",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            physicalHazards: selection.physicalHazards,
          };
        },
        render(data) {
          return (
            PhysicalHazardOrder.filter((ph) => data.physicalHazards?.includes(ph))
              ?.map((hazard) => `- ${PhysicalHazardLabels[hazard]}`)
              .join("\n") ?? ""
          );
        },
      },
      {
        name: "healthHazards",
        label: "Health Hazards",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            healthHazards: selection.healthHazards,
          };
        },
        render(data) {
          return (
            HealthHazardOrder.filter((hh) => data.healthHazards?.includes(hh))
              ?.map((hazard) => `- ${HealthHazardLabels[hazard]}`)
              .join("\n") ?? ""
          );
        },
      },
      {
        name: "manufacturerSupplier",
        label: "Manufacturer/Supplier",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            manufacturerSupplier: selection.manufacturerSupplier,
          };
        },
        render(data) {
          return data.manufacturerSupplier ?? "";
        },
      },
      {
        name: "recommendedUse",
        label: "Recommended Use",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            recommendedUse: selection.recommendedUse,
          };
        },
        render(data) {
          return data.recommendedUse ?? "";
        },
      },
      {
        name: "version",
        label: "Version",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            version: selection.version,
          };
        },
        render(data) {
          return data.version ?? "";
        },
      },
      {
        name: "revisionDate",
        label: "Revision Date",
        strategy: "exclusive",
        merge(selection, mergedRecord) {
          return {
            ...mergedRecord,
            revisionDate: selection.revisionDate,
          };
        },
        render(data) {
          return data.revisionDate ?? "";
        },
      },
      ...stateFieldFields,
    ];
  }, [stateFieldFields]);

  const candidates = useMemo<Candidate<ChemicalInput>[]>(
    () =>
      data?.chemicalsByIds.map<Candidate<ChemicalInput>>((c) => ({
        id: c.id,
        associatedFacilityIds: c.associatedFacilities.map((f) => f.id),
        issues: c.issues,
        data: {
          name: c.name,
          casNumber: c.casNumber,
          alternateId: c.alternateId,
          ehsForm: c.ehsForm,
          stateOfMatter: c.stateOfMatter,
          density: c.density,
          densityUnits: c.densityUnits,
          isEhs: c.isEhs,
          noHazardsNotReporting: c.noHazardsNotReporting,
          isTradeSecret: c.isTradeSecret,
          pureOrMixture: c.pureOrMixture,
          components: c.components,
          physicalHazards: c.physicalHazards,
          healthHazards: c.healthHazards,
          manufacturerSupplier: c.manufacturerSupplier,
          recommendedUse: c.recommendedUse,
          version: c.version,
          revisionDate: c.revisionDate,
          documents: c.documents?.map((d) => ({
            chemicalId: c.id,
            documentId: d.documentId,
            id: d.id,
            key: d.key,
            name: d.name,
            document: {
              id: d.document.id,
              description: d.document.description,
              title: d.document.title,
              storageLink: d.document.storageLink,
              documentType: d.document.documentType,
              fileExtension: d.document.fileExtension,
              sizeMegabytes: d.document.sizeMegabytes,
              authoredAt: d.document.authoredAt,
            },
          })),

          stateFields: c.stateFields?.map((sf) => ({
            jurisdiction: sf.jurisdiction,
            key: sf.key,
            type: ProgramArea.Epcra,
            value: sf.value,
          })),
        },
        name: c.name,
      })) ?? [],
    [data?.chemicalsByIds]
  );

  const validator = useChemicalInputValidation(true);

  const [pendingMerge, setPendingMerge] = useState<{
    input: ChemicalInput;
    mergedIds: string[];
    inventoryOverlapResolutions: InventoryOverlapResolutionInput[] | null;
    needsExclusionAcknowledgement: boolean;
    filedInventoryOverlaps: InventoryOverlap[];
    impact: MergeImpact;
    outcome: MergeOutcome;
  } | null>(null);
  const [acknowledgementReason, setAcknowledgementReason] = useState("");
  const [filedYearsAcknowledged, setFiledYearsAcknowledged] = useState(false);
  const [pendingInventoryOverlaps, setPendingInventoryOverlaps] = useState<{
    input: ChemicalInput;
    mergedIds: string[];
    inventoryOverlaps: InventoryOverlap[];
  } | null>(null);
  const [inventoryOverlapResolutionState, setInventoryOverlapResolutionState] = useState<
    Record<string, InventoryOverlapResolutionState>
  >({});
  // The Step-2 free-entry density for cross-unit combines, seeded from
  // whatever the Step-1 selection already carries. Its value (falling back to
  // the merged record's) is the single density that flows into input.density
  // — there's no separate Step-1/Step-2 divergence.
  const [combineDensityOverride, setCombineDensityOverride] = useState<{
    density: number | null;
    densityUnits: DensityUnits | null;
  }>({ density: null, densityUnits: null });

  const mergedHasDensity =
    (combineDensityOverride.density ?? pendingInventoryOverlaps?.input.density ?? null) != null &&
    (combineDensityOverride.densityUnits ?? pendingInventoryOverlaps?.input.densityUnits ?? null) !=
      null;

  // Step 1's own density, ignoring the inline Step-2 entry — the inline block
  // stays visible while it is the thing supplying the density.
  const mergedInputHasDensity =
    pendingInventoryOverlaps?.input.density != null &&
    pendingInventoryOverlaps?.input.densityUnits != null;

  // Feeds resolveCombineTargetUnit inside combineRequiresDensity — the merged
  // record's classification, not its (separately-tracked) density.
  const mergedProfile = useMemo<CombineTargetProfile>(
    () => ({
      stateOfMatter: pendingInventoryOverlaps?.input.stateOfMatter ?? null,
      physicalHazards: pendingInventoryOverlaps?.input.physicalHazards ?? null,
    }),
    [pendingInventoryOverlaps?.input.stateOfMatter, pendingInventoryOverlaps?.input.physicalHazards]
  );

  const submitMerge = async (
    input: ChemicalInput,
    mergedIds: string[],
    reason: string | null,
    inventoryOverlapResolutions: InventoryOverlapResolutionInput[] | null,
    acknowledgeFiledYearChanges: boolean,
    impact: MergeImpact,
    outcome: MergeOutcome
  ) => {
    return await mergeChemicals({
      variables: {
        ids: mergedIds,
        input,
        acknowledgementReason: reason,
        inventoryOverlapResolutions,
        acknowledgeFiledYearChanges,
      },
      onCompleted: () => {
        alerts.success(
          <Stack spacing={1}>
            <Typography variant="body2">Chemicals merged successfully.</Typography>
            <MergeImpactSummary mode="outcome" impact={impact} outcome={outcome} />
          </Stack>,
          { autoHideDuration: null }
        );
        props.onClose();
        setPendingInventoryOverlaps(null);
        setPendingMerge(null);
      },
      onError: (error: ApolloError) => {
        const chemicalsInProductError = error.graphQLErrors.find(
          (e) => e.extensions?.code === "MULTIPLE_CHEMICALS_IN_PRODUCTS"
        );
        const chemicalsAtFacilityInYear = error.graphQLErrors.find(
          (e) => e.extensions?.code === "MULTIPLE_CHEMICALS_AT_FACILITY_IN_REPORTING_YEAR"
        );
        const combineRequiresDensityError = error.graphQLErrors.find(
          (e) => e.extensions?.code === "COMBINE_REQUIRES_DENSITY"
        );
        const filedYearAcknowledgementError = error.graphQLErrors.find(
          (e) => e.extensions?.code === "MERGE_REQUIRES_FILED_YEAR_ACKNOWLEDGEMENT"
        );
        const measurementStorageLocationCollisionError = error.graphQLErrors.find(
          (e) => e.extensions?.code === "MEASUREMENT_STORAGE_LOCATION_COLLISION"
        );

        if (chemicalsInProductError) {
          const extraInfo = extraInfoStrings(chemicalsInProductError.extensions?.extraInfo);
          alerts.error(
            `The following products cannot be merged. Please update the products such that only one of the merge chemicals is defined on the products and then retry the merge.
            ${extraInfo.join("\n")}`
          );
          return;
        } else if (chemicalsAtFacilityInYear) {
          const extraInfo = extraInfoStrings(chemicalsAtFacilityInYear.extensions?.extraInfo);
          alerts.error(
            `The following facility reporting years cannot be merged. Please update the facility inventory for the reporting year such that only one of the merge chemicals is at the facility and then retry the merge.
            ${extraInfo.join("\n")}`
          );
          return;
        } else if (combineRequiresDensityError) {
          const extraInfo = extraInfoStrings(combineRequiresDensityError.extensions?.extraInfo);
          alerts.error(
            `Enter a density for the merged chemical to combine the following facility reporting years, then retry the merge.
            ${extraInfo.join("\n")}`
          );
          return;
        } else if (filedYearAcknowledgementError) {
          // Safety net: the UI gates this the same way (see continueMerge), so
          // this only fires when server state changed underneath the picker.
          const extraInfo = extraInfoStrings(filedYearAcknowledgementError.extensions?.extraInfo);
          alerts.error(
            `This merge changes inventory for reports that were filed after these overlaps loaded. Close and reopen the merge to acknowledge the change.
            ${extraInfo.join("\n")}`
          );
          return;
        } else if (measurementStorageLocationCollisionError) {
          // Safety net: the client-side gate (hasMeasurementStorageLocationCollision)
          // disables Combine for these, so this only fires if server-side
          // inventory changed underneath the picker.
          const extraInfo = extraInfoStrings(
            measurementStorageLocationCollisionError.extensions?.extraInfo
          );
          alerts.error(
            `Unable to combine measurement inventories that share a storage location. Choose Keep one for the following facility-years instead, then retry the merge.
            ${extraInfo.join("\n")}`
          );
          return;
        }

        console.error(error);
        alerts.error(error.message || "An error occurred while merging the chemicals");
      },
    });
  };

  const continueMerge = async (
    input: ChemicalInput,
    mergedIds: string[],
    inventoryOverlapResolutions: InventoryOverlapResolutionInput[] | null
  ): Promise<void> => {
    const inventoryOverlaps = pendingInventoryOverlaps?.inventoryOverlaps ?? [];
    const candidatesForOutcome = (data?.chemicalsByIds ?? []).filter((c) =>
      mergedIds.includes(c.id)
    );
    const { impact, outcome } = summarizeMergeOutcome(
      candidatesForOutcome,
      inventoryOverlaps,
      inventoryOverlapResolutionState
    );

    const needsExclusionAcknowledgement = mergeRequiresExclusionAcknowledgement(input);
    // Every resolved overlap gets a strategy, so a filed overlap here always
    // means the merge changes that facility-year's inventory. Mirrors the
    // review step's gate; the resolver enforces the same rules.
    const filedInventoryOverlaps = inventoryOverlaps.filter((overlap) => overlap.isFiled);
    setPendingMerge({
      input,
      mergedIds,
      inventoryOverlapResolutions,
      needsExclusionAcknowledgement,
      filedInventoryOverlaps,
      impact,
      outcome,
    });
  };

  // Leaving step 1 re-arms the acknowledgements: the candidate set or merged
  // fields may have changed what's being acknowledged. Round trips between
  // the review and overlaps steps preserve them — nothing on step 2 changes
  // which filed years are affected or whether the exclusion applies.
  const resetAcknowledgements = () => {
    setAcknowledgementReason("");
    setFiledYearsAcknowledged(false);
  };

  // MergePicker requires an onMerge prop, but interceptMerge below always
  // returns true, so this never actually runs — it exists only to satisfy
  // that type. Keep it in sync with interceptMerge's no-overlap branch if
  // either one changes.
  const handleMerge = async (input: ChemicalInput, mergedIds: string[]): Promise<void> => {
    resetAcknowledgements();
    await continueMerge(input, mergedIds, null);
  };

  const candidatesInMerge = useMemo(
    () =>
      (data?.chemicalsByIds ?? []).filter((c) =>
        (pendingInventoryOverlaps?.mergedIds ?? []).includes(c.id)
      ),
    [data?.chemicalsByIds, pendingInventoryOverlaps?.mergedIds]
  );

  const affectedFacilityCount = useMemo(
    () => countAffectedFacilities(candidatesInMerge),
    [candidatesInMerge]
  );

  const handleStepMerge = async (): Promise<void> => {
    if (!pendingInventoryOverlaps) return;
    const inventoryOverlapResolutions = buildInventoryOverlapResolutionInputs(
      pendingInventoryOverlaps.inventoryOverlaps,
      inventoryOverlapResolutionState,
      mergedHasDensity,
      mergedProfile
    );
    const { input, mergedIds } = pendingInventoryOverlaps;
    const mergedInput: ChemicalInput = {
      ...input,
      density: combineDensityOverride.density ?? input.density,
      densityUnits: combineDensityOverride.densityUnits ?? input.densityUnits,
    };
    await continueMerge(mergedInput, mergedIds, inventoryOverlapResolutions);
  };

  const trimmedReason = acknowledgementReason.trim();

  const [activeCandidateIds, setActiveCandidateIds] = useState<string[] | null>(null);

  // Step 1's currently-selected candidates (before a merge is attempted), so
  // the impact-map banner reflects live edits to the candidate set.
  const activeCandidates = useMemo(() => {
    const candidateIds = activeCandidateIds ?? props.chemicalIds;
    return (data?.chemicalsByIds ?? []).filter((c) => candidateIds.includes(c.id));
  }, [activeCandidateIds, props.chemicalIds, data?.chemicalsByIds]);

  const step1InventoryOverlaps = useMemo(
    () => findInventoryOverlaps(activeCandidates),
    [activeCandidates]
  );

  const step1Impact = useMemo(
    () => buildMergeImpact(activeCandidates, step1InventoryOverlaps),
    [activeCandidates, step1InventoryOverlaps]
  );

  const hasInventoryOverlaps = step1InventoryOverlaps.length > 0;
  const totalSteps = hasInventoryOverlaps ? 3 : 2;

  // `isMerging` only disables the MERGE button after a re-render, so a rapid
  // double-click could otherwise fire the mutation twice; the ref guards
  // synchronously.
  const isSubmittingReviewRef = useRef(false);
  const handleReviewSave = async (): Promise<void> => {
    if (!pendingMerge || isSubmittingReviewRef.current) return;
    isSubmittingReviewRef.current = true;
    try {
      const {
        input,
        mergedIds,
        inventoryOverlapResolutions,
        needsExclusionAcknowledgement,
        filedInventoryOverlaps,
        impact,
        outcome,
      } = pendingMerge;
      await submitMerge(
        input,
        mergedIds,
        needsExclusionAcknowledgement ? trimmedReason : null,
        inventoryOverlapResolutions,
        filedInventoryOverlaps.length > 0 ? filedYearsAcknowledged : false,
        impact,
        outcome
      );
      setPendingMerge(null);
    } finally {
      isSubmittingReviewRef.current = false;
    }
  };

  // Field label → display value, grouped for the review summary, from the
  // same field configs the picker table uses, applied to the merged input.
  const reviewFieldGroups = useMemo(() => {
    if (!pendingMerge) return [];
    return buildReviewFieldSummaries(mergeFields, pendingMerge.input);
  }, [pendingMerge, mergeFields]);

  const reviewOverlapSummaries = useMemo(() => {
    if (!pendingMerge) return [];
    const overlaps = pendingInventoryOverlaps?.inventoryOverlaps ?? [];
    return overlaps.map((overlap) => {
      const resolution = inventoryOverlapResolutionState[inventoryOverlapKey(overlap)];
      const keptEntry = overlap.entries.find(
        (entry) => entry.chemicalId === resolution?.keepChemicalId
      );
      return {
        facilityId: overlap.facilityId,
        facilityName: overlap.facilityName,
        reportingYear: overlap.reportingYear,
        strategy: resolution?.strategy ?? InventoryOverlapResolutionStrategy.Keep,
        keptChemicalName:
          resolution?.strategy === InventoryOverlapResolutionStrategy.Keep
            ? (keptEntry?.chemicalName ?? null)
            : null,
        isFiled: overlap.isFiled,
        conflictChoices: buildConflictChoiceSummaries(overlap, resolution),
      };
    });
  }, [pendingMerge, pendingInventoryOverlaps, inventoryOverlapResolutionState]);

  // The combine density applies to the whole merged chemical, not any one
  // overlap, so the review step renders it once — only when at least one
  // overlap actually resolved to Combine and an effective density exists
  // (Step 2's inline entry, falling back to Step 1's input, same precedence
  // as mergedHasDensity above).
  const reviewCombineDensity = useMemo(() => {
    if (!pendingMerge) return null;
    const overlaps = pendingInventoryOverlaps?.inventoryOverlaps ?? [];
    const hasCombineResolution = overlaps.some(
      (overlap) =>
        inventoryOverlapResolutionState[inventoryOverlapKey(overlap)]?.strategy ===
        InventoryOverlapResolutionStrategy.Combine
    );
    if (!hasCombineResolution) return null;

    const density =
      combineDensityOverride.density ?? pendingInventoryOverlaps?.input.density ?? null;
    const densityUnits =
      combineDensityOverride.densityUnits ?? pendingInventoryOverlaps?.input.densityUnits ?? null;
    if (density == null || densityUnits == null) return null;

    return { density, densityUnits };
  }, [
    pendingMerge,
    pendingInventoryOverlaps,
    inventoryOverlapResolutionState,
    combineDensityOverride,
  ]);

  // Review wins over overlaps, which wins over the base picker table.
  let activeStep = 0;
  if (pendingMerge) {
    activeStep = totalSteps - 1;
  } else if (pendingInventoryOverlaps) {
    activeStep = 1;
  }

  let stepOverride: { subtitle: string; content: ReactNode } | null = null;
  if (pendingMerge) {
    stepOverride = {
      subtitle: "Review your selections before finalizing the merge.",
      content: (
        <ReviewMergeStep
          impact={pendingMerge.impact}
          fieldGroups={reviewFieldGroups}
          overlapSummaries={reviewOverlapSummaries}
          combineDensity={reviewCombineDensity}
          needsExclusionAcknowledgement={pendingMerge.needsExclusionAcknowledgement}
          acknowledgementReason={acknowledgementReason}
          onAcknowledgementReasonChange={setAcknowledgementReason}
          filedOverlaps={pendingMerge.filedInventoryOverlaps.map((overlap) => ({
            facilityId: overlap.facilityId,
            facilityName: overlap.facilityName,
            reportingYear: overlap.reportingYear,
          }))}
          filedYearsAcknowledged={filedYearsAcknowledged}
          onFiledYearsAcknowledgedChange={setFiledYearsAcknowledged}
          onCancel={requestClose}
          onBack={() => setPendingMerge(null)}
          onSave={handleReviewSave}
          isMerging={isMerging}
          footerStep={{ current: totalSteps, total: totalSteps }}
        />
      ),
    };
  } else if (pendingInventoryOverlaps) {
    stepOverride = {
      subtitle:
        "Choose how to resolve each facility-year where the merged chemicals both report inventory.",
      content: (
        <ResolveInventoryOverlapsStep
          inventoryOverlaps={pendingInventoryOverlaps.inventoryOverlaps}
          resolutions={inventoryOverlapResolutionState}
          onStrategyChange={(key, strategy) =>
            setInventoryOverlapResolutionState((prev) => ({
              ...prev,
              [key]: { strategy, fieldChoices: {} },
            }))
          }
          onKeepSelect={(key, keepChemicalId) =>
            setInventoryOverlapResolutionState((prev) => ({
              ...prev,
              [key]: { ...prev[key], keepChemicalId },
            }))
          }
          onFieldChoice={(key, conflictKey, choice) =>
            setInventoryOverlapResolutionState((prev) => ({
              ...prev,
              [key]: {
                ...prev[key],
                fieldChoices: { ...prev[key]?.fieldChoices, [conflictKey]: choice },
              },
            }))
          }
          onBatchStrategyChange={(strategy) =>
            setInventoryOverlapResolutionState((prev) => {
              const next = { ...prev };
              for (const inventoryOverlap of pendingInventoryOverlaps.inventoryOverlaps) {
                if (inventoryOverlap.isFiled) continue;
                if (
                  strategy === InventoryOverlapResolutionStrategy.Combine &&
                  !isBatchCombineEligible(inventoryOverlap)
                ) {
                  continue;
                }
                const key = inventoryOverlapKey(inventoryOverlap);
                next[key] = {
                  strategy,
                  keepChemicalId: prev[key]?.keepChemicalId,
                  fieldChoices: {},
                };
              }
              return next;
            })
          }
          onBack={() => setPendingInventoryOverlaps(null)}
          onCancel={requestClose}
          footerStep={{ current: 2, total: 3 }}
          onMerge={handleStepMerge}
          isMerging={isMerging}
          mergedChemicalName={pendingInventoryOverlaps.input.name ?? ""}
          affectedFacilityCount={affectedFacilityCount}
          mergedHasDensity={mergedHasDensity}
          mergedInputHasDensity={mergedInputHasDensity}
          combineDensity={combineDensityOverride}
          onCombineDensityChange={setCombineDensityOverride}
          mergedProfile={mergedProfile}
        />
      ),
    };
  }

  return (
    <>
      <MergePicker
        open={props.open}
        onClose={requestClose}
        kind={"chemical"}
        fields={mergeFields}
        candidates={candidates}
        // TODO: I don't like that we have to cast the type here, but this is because we want to share the chemical input validator. I think this is a code smell, and should be addressed in the validator directly.
        validator={(result) => validator(result as ChemicalDetailInputs)}
        onMerge={handleMerge}
        loadingCandidates={loadingChemicals}
        isMerging={isMerging}
        onCandidatesChange={setActiveCandidateIds}
        mergeButtonContent={"CONTINUE"}
        footerStep={{ current: 1, total: totalSteps }}
        howToOpen={howToOpen}
        onHowToOpenChange={(open) => {
          howToConsumedRef.current = true;
          setHowToOpen(open);
          if (!open) void markDismissed();
        }}
        bannerContent={
          pendingInventoryOverlaps || pendingMerge ? undefined : (
            <MergeImpactSummary mode="preview" impact={step1Impact} />
          )
        }
        interceptMerge={(input, mergedIds) => {
          resetAcknowledgements();
          const inventoryOverlaps = findInventoryOverlaps(
            (data?.chemicalsByIds ?? []).filter((c) => mergedIds.includes(c.id))
          );
          if (inventoryOverlaps.length === 0) {
            void continueMerge(input, mergedIds, null);
            return true;
          }
          // Reconcile instead of resetting so selections survive a trip back
          // to the review step.
          setInventoryOverlapResolutionState((prev) =>
            reconcileInventoryOverlapResolutions(inventoryOverlaps, prev)
          );
          setCombineDensityOverride((previous) => reconcileCombineDensityOverride(previous, input));
          setPendingInventoryOverlaps({ input, mergedIds, inventoryOverlaps });
          return true;
        }}
        stepper={{
          steps: hasInventoryOverlaps
            ? ["Review details", "Resolve inventory overlaps", "Review & confirm"]
            : ["Review details", "Review & confirm"],
          activeStep,
        }}
        stepOverride={stepOverride}
      />
      <ConfirmDialog
        open={confirmCancelOpen}
        header="Cancel this merge?"
        info={
          <Typography component="p">
            Your selections will be discarded and no chemicals will be merged.
          </Typography>
        }
        confirmText="Yes, Cancel Merge"
        cancelText="Back"
        onClose={() => setConfirmCancelOpen(false)}
        onConfirm={() => {
          setConfirmCancelOpen(false);
          props.onClose();
        }}
      />
    </>
  );
}
